import { contentTypes } from '../../../../constants';
import { getItem, setItem } from '../../../../utils/storage';
import { equals, reject } from 'ramda';

export const MAX_RECENT_ITEMS_CACHE_COUNT = 20;

export const SINGLE_ELEMENT_VIEW = 'SINGLE_ELEMENT';
export const ELEMENT_LIST_VIEW = 'ELEMENT_LIST';

// Only fetch this many Values or Types when initially showing the list view in the Value Picker
// This is because some tenants have a large number of values and the payload is large.
export const INITIAL_LIMIT = 11;

/*
 * List of available Value Types
 *
 * requiresType: Indicates if the Value Type requires a Type specification, i.e. List & Object need a Type
 * canBeFormatted: Indicates if the Value can have user-defined formatting
 */
export const VALUE_TYPES = [
    { value: 'ContentString', inputType: 'text', label: 'String', requiresType: false, canBeFormatted: true },
    { value: 'ContentNumber', inputType: 'number', label: 'Number', requiresType: false, canBeFormatted: true },
    { value: 'ContentPassword', inputType: 'password', label: 'Password', requiresType: false, canBeFormatted: false },
    { value: 'ContentBoolean', inputType: 'checkbox', label: 'Boolean', requiresType: false, canBeFormatted: false },
    { value: 'ContentEncrypted', inputType: 'text', label: 'Encrypted', requiresType: false, canBeFormatted: false },
];

const contentTypeToTypeDisplay = (contentType) => {
    switch (contentType.toUpperCase()) {
    case contentTypes.boolean:
        return 'Boolean';
    case contentTypes.content:
        return 'Content';
    case contentTypes.datetime:
        return 'DateTime';
    case contentTypes.list:
        return 'List';
    case contentTypes.number:
        return 'Number';
    case contentTypes.object:
        return 'Object';
    case contentTypes.password:
        return 'Password';
    case contentTypes.encrypted:
        return 'Encrypted';
    case contentTypes.string:
        return 'String';
    default:
        return null;
    }
};

/**
 *
 * @param {String} id
 * @param {String} developerName The values name
 * @param {String} typeElementPropertyId The Id of the values property (if applicable)
 * @param {String} typeElementPropertyDeveloperName The name of the values property (if applicable)
 * @param {String} contentType The content type of the value e.g. ContentString etc
 * @param {String} parentContentType If the value is a reference (value property) then it will have a parent content type e.g. ContentObject
 * @param {String} typeElementDeveloperName If the value has a parent then this is the name of its associated type
 * @param {String} serviceElementDeveloperName The name of the service associated to the values type
 *
 * @description Formats raw value metadata so that it is suitable for displaying
 * additional information about the value when rendered in the picker
 */
export const dataToValueOption = ({
    id,
    developerName,
    typeElementPropertyId: propertyId,
    typeElementPropertyDeveloperName: propertyName,
    contentType,
    parentContentType,
    typeElementDeveloperName: objectType,
    serviceElementDeveloperName: serviceName,
}) => {

    const elementName = propertyName ? `${developerName} / ${propertyName}` : `${developerName}`;
    const parentType = parentContentType ? contentTypeToTypeDisplay(parentContentType) : null;
    const type = contentTypeToTypeDisplay(contentType);

    const parentTypeText = parentType ? `${parentType} of ` : '';
    const objectTypeText = objectType ? `${objectType}` : '';
    const serviceTypeText = serviceName ? ` from  ${serviceName}` : '';
    const separator = objectType ? ' / ' : '';

    const elementMeta = `
    ${parentTypeText}
    ${objectTypeText}
    ${serviceTypeText}
    ${separator}
    ${type}
    `;

    return { id, elementName, elementMeta, parentContentType, developerName, contentType, propertyId, propertyName };
};

/**
 *
 * @param {{
 *      valueId: String,
 *      propertyId: String
 * }} newValueReference The new value reference to add
 *
 * @description Adds a new value reference to the top of the recent value references stack
 */
export const addRecentValueReferenceId = ({ valueId = null, propertyId = null }) => {

    if (valueId === null) {
        return;
    }

    const newValueReference = { valueId, propertyId };
    // Get recently selected value references from storage
    const recentValueReferences = getItem(localStorage, 'values-recent') || [];
    // Remove a matching entry if exists - relies on preserving the order of items
    const filteredValueReferences = reject(
        equals(newValueReference),
        recentValueReferences,
    );

    // Push the new reference on top of the stack
    filteredValueReferences.unshift(newValueReference);

    setItem(localStorage, 'values-recent', filteredValueReferences.slice(0, MAX_RECENT_ITEMS_CACHE_COUNT));
};

/**
 * @description Gets list of value objects which have been used recently
 *
 * @returns A list of recently used values ordered by when they were last used (most recent first)
 */
export const getRecentValueReferenceIds = () => getItem(localStorage, 'values-recent') || [];

/**
 *
 * @param {String} id
 * @param {String} developerName The types name
 * @param {String} serviceElementDeveloperName The name of the service associated to the type
 *
 * @description Formats raw type metadata so that it is suitable for displaying
 * additional information about the type when rendered in the picker
 */
export const dataToTypeOption = ({
    id,
    developerName,
    serviceElementDeveloperName: serviceName,
}) => {

    const serviceTypeText = serviceName ? ` from  ${serviceName}` : '';

    const elementMeta = `
    ${developerName}
    ${serviceTypeText}
    `;

    return { id, elementName: developerName, elementMeta };
};

export const ELEMENT_TYPES = {
    value: 'value',
    type: 'type',
};
